import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useSocket } from '../contexts/SocketContext';
import TabBar from '../components/TabBar';
import TutorialButton from '../components/TutorialButton';
import BizSwitch from '../components/BizSwitch';
import PuzzleClaimPanel from '../components/PuzzleClaimPanel';
import { useBizMode } from '../utils/bizMode';
import api from '../utils/api';
import { Database, AlertTriangle, Send, TrendingUp, TrendingDown, Gauge } from 'lucide-react';


// 今日额度卡：状态 → 颜色 / 背景 / 用户看到的名称 / 一句话提示
// （面向普通用户：只说“现在能不能提、为什么”，不讲内部规则）
const QUOTA_COLOR = {
  unlimited: 'var(--c-primary)',
  starter:   'var(--c-primary)',
  unlocked:  'var(--c-success)',
  incentive: 'var(--c-accent)',
  suspended: 'var(--c-danger)',
  capped:    'var(--c-danger)',
};
const QUOTA_BG = {
  unlimited: 'rgba(0,122,255,0.06)',
  starter:   'rgba(0,122,255,0.06)',
  unlocked:  'rgba(52,199,89,0.06)',
  incentive: 'rgba(255,149,0,0.06)',
  suspended: 'rgba(255,59,48,0.06)',
  capped:    'rgba(255,59,48,0.08)',
};
const QUOTA_TEXT = {
  unlimited: { label: '不限量',   hint: '不受每日额度限制。' },
  starter:   { label: '正常提取', hint: '今日基础额度还没提完，可以继续提取。' },
  unlocked:  { label: '已达标',   hint: '成功率已达标，可以继续提取。' },
  incentive: { label: '待达标',   hint: '成功率还没达标，先多给你一点提取机会。成功率提上来后可继续提取。' },
  suspended: { label: '今日暂停', hint: '成功率未达标，暂时不能提取。请先完成并上传已提取的数据，等图片识别完成、成功率达标后再来。' },
  capped:    { label: '今日已满', hint: '今日提取已达上限，请明天再来。' },
};

export default function RequestPage() {
  const [quantity, setQuantity] = useState('');
  const [loading, setLoading] = useState(false);
  const [poolInfo, setPoolInfo] = useState(null);
  const [hasActive, setHasActive] = useState(false);
  const [quota, setQuota] = useState(null);
  const navigate = useNavigate();
  const [bizMode] = useBizMode(); // 排五 / 证券
  const { showToast } = useToast();
  const { subscribe } = useSocket();
  const [workSettings, setWorkSettings] = React.useState(null);

  const fetchInfo = useCallback(async () => {
    try {
      const [poolRes, summaryRes] = await Promise.all([
        api.get('/data/pool/stats'),
        api.get('/dispatch/my-summary')
      ]);
      setPoolInfo(poolRes.data);
      setHasActive(summaryRes.data.hasActiveBatch);
    } catch (err) { console.error(err); }
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const res = await api.get('/dispatch/quota');
      setQuota(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const fetchWorkSettings = useCallback(() => {
    api.get('/admin/settings/work-hours').then(res => setWorkSettings(res.data)).catch(() => {});
  }, []);

  useEffect(() => { fetchInfo(); fetchQuota(); fetchWorkSettings(); }, [fetchInfo, fetchQuota, fetchWorkSettings]);

  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => {
      if (event === 'pool_updated' || event === 'data_refresh' || event === 'stats_refresh') fetchInfo();
      if (event === 'work_hours_updated') fetchWorkSettings();
      // OCR 跑完或管控配置变更 → 刷新配额
      if (event === 'ocr_batch_done' || event === 'rate_ctrl_updated' || event === 'data_refresh') fetchQuota();
    });
  }, [subscribe, fetchInfo, fetchQuota, fetchWorkSettings]);

  const quickAmounts = [10, 30, 50];

  // 单次最多受配额约束，且绝不超 100。
  // 配额管控关闭时，后台“单次绝对上限”依然生效（服务端 /dispatch/quota 会返回它），
  // 这里必须跟服务端一致，不能写死 100，否则会出现“填 100 却只给 50”。
  const maxAllowed = quota
    ? (quota.enabled
        ? Math.min(100, quota.per_request_limit || 0)
        : Math.min(100, quota.per_request_limit || 100))
    : 100;

  const handleSubmit = async () => {
    const qty = parseInt(quantity);
    if (!qty || qty <= 0) { showToast('请输入有效的接量数', 'error'); return; }
    if (qty > maxAllowed) {
      showToast(maxAllowed > 0
        ? `本次最多可提 ${maxAllowed} 条，请调整数量`
        : (QUOTA_TEXT[quota?.status]?.hint || '暂时不能提取'), 'error');
      return;
    }
    setLoading(true);
    try {
      const res = await api.post('/dispatch/request', { quantity: qty });
      showToast(`成功接取 ${res.data.dispatched} 条数据`, 'success');
      if (res.data.partial) showToast(`池中数据不足，实际派发 ${res.data.dispatched} 条`, 'info');
      else if (res.data.dispatched < qty) showToast(`已按单次上限派发 ${res.data.dispatched} 条`, 'info');
      if (res.data.quota) setQuota(res.data.quota);
      setQuantity('');
      setTimeout(() => navigate('/'), 800);
    } catch (err) {
      showToast(err.response?.data?.error || '提交失败', 'error');
    } finally { setLoading(false); }
  };



  // 配额进度条（已用 / 总量）
  const quotaPercent = quota?.enabled && quota.daily_cap
    ? Math.min(100, Math.round((quota.daily_used / quota.daily_cap) * 100))
    : null;

  return (
    <div className="page">
      <div className="header" style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ flex: 1 }}>提交接量</span>
        <TutorialButton />
      </div>

      {/* 排五 / 证券 业务切换 */}
      <div style={{ padding: '10px 16px 0' }}><BizSwitch /></div>

      {bizMode === 'puzzle' ? (
        <div style={{ padding: '12px 16px' }}><PuzzleClaimPanel /></div>
      ) : (
      <div style={{ padding: '12px 16px' }}>
        {/* Pool info */}
        <div className="card" style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1, background: 'var(--c-primary-light)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, marginBottom: 6 }}>
              <Database size={14} color="var(--c-primary)" />
              <span style={{ fontSize: 11, color: 'var(--c-text-secondary)', fontWeight: 600 }}>池中可用</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--c-primary)' }}>{poolInfo?.available || 0}</div>
          </div>
        </div>

        {/* 今日额度卡（开启管控时显示）：只告诉用户“现在能提多少、为什么” */}
        {quota?.enabled && (() => {
          const st = QUOTA_TEXT[quota.status] || {};
          const color = QUOTA_COLOR[quota.status] || 'var(--c-primary)';
          const unlimited = quota.status === 'unlimited';
          // 今天还没提取时成功率无意义，显示“—”，避免一上来就是红色的 0.0%
          const hasRate = !unlimited && quota.success_rate !== null && quota.daily_used > 0;
          return (
            <div className="card" style={{
              background: QUOTA_BG[quota.status] || 'var(--c-card)',
              border: `1px solid ${color}33`,
              padding: '12px 14px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Gauge size={15} color={color} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>今日额度</span>
                </div>
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                  background: `${color}18`,
                  color,
                }}>{st.label || quota.status_label}</span>
              </div>

              {/* 今日已提 / 每日上限（已豁免的用户没有上限，只显示已提数量） */}
              {quota.daily_cap != null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1, height: 6, background: 'var(--c-divider)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: 4,
                      width: `${quotaPercent}%`,
                      background: quotaPercent >= 90 ? 'var(--c-danger)' : quotaPercent >= 60 ? 'var(--c-accent)' : 'var(--c-success)',
                      transition: 'width 0.4s',
                    }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--c-text-secondary)', flexShrink: 0 }}>
                    今日已提 {quota.daily_used} / 上限 {quota.daily_cap} 条
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: 'var(--c-text-secondary)', marginBottom: 8 }}>
                  今日已提 {quota.daily_used} 条
                </div>
              )}

              <div style={{ display: 'flex', gap: 12 }}>
                {!unlimited && (
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}>成功率</div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: hasRate
                      ? (quota.success_rate >= quota.target_rate ? 'var(--c-success)' : 'var(--c-danger)')
                      : 'var(--c-text-secondary)',
                      display: 'flex', alignItems: 'center', gap: 4 }}>
                      {hasRate ? (
                        <>
                          {(quota.success_rate * 100).toFixed(1)}%
                          {quota.success_rate >= quota.target_rate
                            ? <TrendingUp size={12} />
                            : <TrendingDown size={12} />}
                        </>
                      ) : '—'}
                      <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--c-text-tertiary)' }}>
                        目标 {(quota.target_rate * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--c-text-secondary)' }}>本次最多可提</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: quota.per_request_limit > 0 ? color : 'var(--c-danger)' }}>
                    {quota.per_request_limit} 条
                  </div>
                </div>
              </div>

              {st.hint && (
                <div style={{ fontSize: 12, color, marginTop: 8, lineHeight: 1.5 }}>{st.hint}</div>
              )}
              {!unlimited && (
                <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 4 }}>
                  成功率 = 已提取的数据中，图片识别成功的占比
                </div>
              )}
            </div>
          );
        })()}

        {hasActive ? (
          <div className="card" style={{ background: 'rgba(255,149,0,0.06)', border: '1px solid rgba(255,149,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={18} color="var(--c-accent)" />
              <div>
                <p style={{ fontWeight: 600, color: '#9a6400', fontSize: 14 }}>您有未完成的任务</p>
                <p style={{ fontSize: 13, color: '#9a6400', marginTop: 2, opacity: 0.8 }}>请先在工作台完成当前任务</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="section-title" style={{ paddingTop: 4 }}>输入接量数</div>

            <input
              className="input"
              type="number"
              min="1"
              max={maxAllowed}
              placeholder={maxAllowed > 0 ? `本次最多 ${maxAllowed} 条` : '暂时不能提取'}
              value={quantity}
              onChange={e => setQuantity(e.target.value)}
              style={{ fontSize: 24, textAlign: 'center', fontWeight: 700, padding: 16, marginTop: 8 }}
            />

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginTop: 14 }}>
              {quickAmounts.map(amt => (
                <button key={amt} className="btn btn-outline btn-sm"
                  onClick={() => setQuantity(String(Math.min(amt, maxAllowed)))}
                  disabled={(quota?.enabled && quota.status === 'capped') || maxAllowed <= 0}
                  style={{ fontSize: 15, fontWeight: 700, padding: '10px 0', borderRadius: 10 }}>
                  {amt}
                </button>
              ))}
            </div>


            <button className="btn btn-primary" onClick={handleSubmit}
              disabled={loading || !quantity || (quota?.enabled && quota.status === 'capped')}
              style={{ marginTop: 14, height: 50, fontSize: 16, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              <Send size={18} /> {loading ? '派发中...' : '确认提交'}
            </button>
          </div>
        )}
      </div>
      )}

      <TabBar mode="user" />
    </div>
  );
}
